// ./src/components/features/Users/UserFormModal/UserFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  TextInput,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  PasswordInput,
} from "@mantine/core";

import { User, UserInput } from "@/utils/api";

import { StatusSelector } from "@/components/features";
import { AppModal, AppIcon } from "@/components/generic";

export interface UserFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** User to edit (if null/undefined, it's create mode) */
  user?: User | null;

  /** Callback when user should be created */
  onCreateUser?: (userData: UserInput) => Promise<User | null>;

  /** Callback when user should be updated */
  onUpdateUser?: (userId: string, userData: UserInput) => Promise<User | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from user operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface UserFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  repeatPassword: string;
  status: number | null;
}

const initialFormData: UserFormData = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  repeatPassword: "",
  status: 100,
};

export const UserFormModal: React.FC<UserFormModalProps> = ({
  opened,
  onClose,
  user,
  onCreateUser,
  onUpdateUser,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(user) && Boolean(user?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or user changes
  useEffect(() => {
    if (opened) {
      if (user) {
        // Edit mode - populate form with user data
        setFormData({
          firstName: user.firstName || "",
          middleName: user.middleName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phone: (user as any).phone || "",
          password: "",
          repeatPassword: "",
          status: user.status != null ? user.status : 100,
        });
      } else {
        // Create mode - reset to initial values
        setFormData(initialFormData);
      }
      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, user, onClearError]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Password validation - only required for create mode
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }

      if (!formData.repeatPassword) {
        newErrors.repeatPassword = "Please confirm your password";
      } else if (formData.password !== formData.repeatPassword) {
        newErrors.repeatPassword = "Passwords do not match";
      }
    }

    // If password is being changed in edit mode, validate matching
    if (isEditMode && formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      }
      if (formData.password !== formData.repeatPassword) {
        newErrors.repeatPassword = "Passwords do not match";
      }
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    field: keyof UserFormData,
    value: string | number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Get the error to display for a field
  const getDisplayError = (
    field: keyof UserFormData
  ): string | number | null | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    // Clear errors on new submission
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare user data
      const userData: UserInput = {
        firstName: formData.firstName.trim(),
        middleName: formData.middleName.trim() || null,
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        status: formData.status,
      };

      // Add phone if available (cast to any since it might not be in UserInput type)
      if (formData.phone.trim()) {
        (userData as any).phone = formData.phone.trim();
      }

      // Add password fields if they have values
      if (formData.password) {
        userData.password = formData.password;
        userData.repeatPassword = formData.repeatPassword;
      }

      let result: User | null = null;

      if (isEditMode && user && onUpdateUser) {
        // Update existing user
        result = await onUpdateUser(user.id || "", userData);
      } else if (!isEditMode && onCreateUser) {
        // Create new user
        result = await onCreateUser(userData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      // Error handling is managed by the hook/parent component
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Render generic errors at the bottom of the form
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit User" : "Add New User"}
      size="lg"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Name Fields */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First Name"
              placeholder="Enter first name"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              error={getDisplayError("firstName")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last Name"
              placeholder="Enter last name"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              error={getDisplayError("lastName")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Email and Phone */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={getDisplayError("email")}
              type="email"
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              error={getDisplayError("phone")}
              type="tel"
              size="md"
            />
          </Grid.Col>
        </Grid>

        {/* Password Fields - Only show in create mode or if user wants to change password */}
        {!isEditMode && (
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <PasswordInput
                label="Password"
                placeholder="Enter password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                error={getDisplayError("password")}
                required
                withAsterisk
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <PasswordInput
                label="Confirm Password"
                placeholder="Re-enter password"
                value={formData.repeatPassword}
                onChange={(e) =>
                  handleInputChange("repeatPassword", e.target.value)
                }
                error={getDisplayError("repeatPassword")}
                required
                withAsterisk
                size="md"
              />
            </Grid.Col>
          </Grid>
        )}

        {isEditMode && (
          <Grid>
            <Grid.Col span={12}>
              <PasswordInput
                label="New Password"
                placeholder="Leave empty to keep current password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                error={getDisplayError("password")}
                description="Only fill this if you want to change the password"
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <PasswordInput
                label="Confirm New Password"
                placeholder="Re-enter new password"
                value={formData.repeatPassword}
                onChange={(e) =>
                  handleInputChange("repeatPassword", e.target.value)
                }
                error={getDisplayError("repeatPassword")}
                disabled={!formData.password}
                size="md"
              />
            </Grid.Col>
          </Grid>
        )}

        {/* Status Configuration */}
        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value) => handleInputChange("status", Number(value))}
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Generic errors */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
